import * as React from 'react';
import { Col, Row, Divider, Input, Checkbox, Select } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import Form, { FormItem } from '../../../components/Form';
import { Title } from '../../../components/Layout/components';
import OrderStateSelect from '../../../components/Others/OrderStateSelect';
import Documentation from './components/Documentation';
import { getLetterDataTypes } from 'helpers/src/letterData/letterDataLetterTypes';

interface ILetterDataDetailProps {
  domain: string;
  letterData: GQL.ILetterData;
  languages: Array<GQL.ILanguage>;
  onSave: (letterData: GQL.ILetterData) => void;
  onCancel: () => void;
}

interface ILetterDataDetailState {
  letterData: GQL.ILetterData;
  currentLanguage: string;
  openDocumentation: boolean;
}

export default class LetterDataDetail extends React.Component<ILetterDataDetailProps, ILetterDataDetailState> {

  public constructor(props: ILetterDataDetailProps) {
    super(props);

    this.state = {
      letterData: props.letterData || {},
      currentLanguage: props.languages.map(l => l.value)[0],
      openDocumentation: false,
    };

    this.onChange = this.onChange.bind(this);
    this.save = this.save.bind(this);
    this.toggleOpenDocumentation = this.toggleOpenDocumentation.bind(this);
  }

  public render() {
    const { languages } = this.props;
    const { letterData, currentLanguage } = this.state;
    const letterTypesRaw = getLetterDataTypes();
    const letterTypes = Object.keys(letterTypesRaw).filter(key => letterTypesRaw.hasOwnProperty(key)).map((key) => ({key, label: letterTypesRaw[key]}));

    return (
      <div>
        <Title
          languages={languages}
          currentLanguage={currentLanguage}
          onChange={(l: string) => { this.setState({ currentLanguage: l }); }}
        >
          {letterData
            ? `Letter data ${letterData.title ? `"${letterData.title}"` : ''}`
            : `Letter data`}
        </Title>

        <Divider />
        <Form
          onSubmit={this.save}
          onCancel={this.props.onCancel}
        >
          <Row>
            <Col md={24}>
              <FormItem label={'Title'} required={true}>
                <Input
                  name={'title'}
                  autoFocus={true}
                  placeholder="Internal title. For example &quot;Zruseno&quot;"
                  required={true}
                  addonBefore={'Internal'}
                  defaultValue={letterData.title || ''}
                  onChange={this.onChange}
                />
              </FormItem>
              <FormItem label={'Subject'} required={true}>
                <Input
                  key={currentLanguage}
                  name={'subject'}
                  addonBefore={currentLanguage}
                  placeholder="E-mail subject"
                  required={true}
                  defaultValue={(letterData.subject || {})[currentLanguage]}
                  onChange={e => this.onChange(e, currentLanguage)}
                />
              </FormItem>
              <FormItem label={'Message'} required={true}>
                <Input.TextArea
                  rows={15}
                  key={currentLanguage}
                  name={'text'}
                  placeholder="E-mail message"
                  required={true}
                  defaultValue={(letterData.text || {})[currentLanguage]}
                  onChange={e => this.onChange(e, currentLanguage)}
                />
                <div style={{ paddingTop: '5px', cursor: 'pointer', color: '#40a9ff' }} onClick={this.toggleOpenDocumentation}>
                  <InfoCircleOutlined style={{ marginRight: 5 }} />
                  Documentation for dynamic content
                </div>
                {this.state.openDocumentation === true &&
                  <Documentation
                    onClose={this.toggleOpenDocumentation}
                  />}
              </FormItem>
              <FormItem style={{ marginTop: 20 }}>
                <Checkbox
                  name={'attachmentInvoice'}
                  checked={letterData.attachmentInvoice !== null && letterData.attachmentInvoice !== undefined ? letterData.attachmentInvoice : false}
                  onChange={e => this.onChange(e)}
                >
                  Insert invoice to email attachment
                </Checkbox>
              </FormItem>
              <FormItem style={{ paddingLeft: 25 }}>
                <Checkbox
                  disabled={letterData.attachmentInvoice !== true}
                  name={'sendInvoiceToCustomer'}
                  checked={letterData.sendInvoiceToCustomer !== null && letterData.sendInvoiceToCustomer !== undefined ? letterData.sendInvoiceToCustomer : false}
                  onChange={e => this.onChange(e)}
                >
                  For auto-send invoice to customer
                </Checkbox>
              </FormItem>
              <FormItem>
                <Checkbox
                  name={'attachmentProforma'}
                  checked={letterData.attachmentProforma !== null && letterData.attachmentProforma !== undefined ? letterData.attachmentProforma : false}
                  onChange={e => this.onChange(e)}
                >
                  Insert proforma invoice to email attachment
                </Checkbox>
              </FormItem>
              <FormItem style={{ paddingLeft: 25 }}>
                <Checkbox
                  disabled={letterData.attachmentProforma !== true}
                  name={'sendProformaToCustomer'}
                  checked={letterData.sendProformaToCustomer !== null && letterData.sendProformaToCustomer !== undefined ? letterData.sendProformaToCustomer : false}
                  onChange={e => this.onChange(e)}
                >
                  For auto-send proforma to customer
                </Checkbox>
              </FormItem>
              
              {this.state.letterData.id === undefined &&
                <FormItem label={'State'}>
                  <OrderStateSelect
                    name={'externalOrderStateId'}
                    style={{ width: '100%' }}
                    onChange={(e: any, orderState: GQL.IExternalOrderState) => this.onChange(e)}
                    value={letterData.externalOrderStateId}
                  />
                </FormItem>}
            </Col>
          </Row>
          <Row>
            <Col md={24}>
              <FormItem label="Letter type">
                <Select
                  defaultValue={letterData.type || undefined}
                  allowClear={true}
                  onChange={(v) => this.onChange({target: {name: 'type', value: v}})}>
                  {letterTypes.map((type) => {return <Select.Option key={type.key} value={type.key}>{type.label}</Select.Option>;})}
                </Select>
              </FormItem>
            </Col> 
          </Row>
        </Form>
      </div>
    );
  }

  private onChange(e: any, language?: string) {
    const { name, value, checked } = e.target;

    const inputValue = (value !== undefined ? value : checked);

    // vše ostatní
    const newValue = (language
      ? { [name]: { ...this.state.letterData[name], [language]: inputValue } }
      : { [name]: inputValue });
    
    // zrušení odesílání faktury zákazníkovi
    if (name === 'attachmentInvoice' && inputValue === false) {
      if (this.state.letterData.sendInvoiceToCustomer === true) {
        (newValue as any).sendInvoiceToCustomer = false;
      }
    }

    // zrušení odesílání proformy zákazníkovi
    if (name === 'attachmentProforma' && inputValue === false) {
      if (this.state.letterData.sendProformaToCustomer === true) {
        (newValue as any).sendProformaToCustomer = false;
      }
    }

    this.setState({
      letterData: { ...this.state.letterData, ...newValue }
    });
  }

  private async save() {
    this.props.onSave({...this.state.letterData});
  }

  private toggleOpenDocumentation() {
    this.setState({
      openDocumentation: !this.state.openDocumentation,
    });
  }
}



// WEBPACK FOOTER //
// ./src/scenes/LetterData/LetterDataDetail/LetterDataDetail.tsx